/**
 * Image Generator Page
 *
 * AI image generation arche with up to 3 simultaneous AI instances,
 * each with independent settings, galleries, and minimize/expand capability.
 *
 * This file is a slim orchestrator that wires together:
 * - useImageGeneratorState (reducer-based state management)
 * - useImageGeneratorSubmit (submission, streaming, polling)
 * - Extracted UI components (panels, sheets, lightbox)
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Send, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useImageModels, useImageModelDefinition } from '@/lib/hooks';
import { useArcheSession } from '@/lib/hooks/useArcheSession';
import { useAuth } from '@/auth';
import { ArcheLayout } from '../../../components/ArcheLayout';
import { ImageInstancePanel } from '../components/ImageInstancePanel';
import { ImageGeneratorHeader } from '../components/ImageGeneratorHeader';
import { ImageGeneratorScrollbar } from '../components/ImageGeneratorScrollbar';
import { ImageSettingsSheet } from '../components/ImageSettingsSheet';
import { ImagePresetsDialog } from '../components/ImagePresetsDialog';
import { PresetEditDialog } from '../components/PresetEditDialog';
import { ModesDialog } from '../components/ModesDialog';
import { ImageLightbox } from '../components/ImageLightbox';
import { ReferenceImageDialog } from '../components/ReferenceImageDialog';
import { useSidebar } from '../../../context/SidebarContext';
import { useImageGeneratorState } from '../hooks/useImageGeneratorState';
import { useImageGeneratorSubmit } from '../hooks/useImageGeneratorSubmit';
import { useImageGeneratorAI } from '../hooks/useImageGeneratorAI';
import { FiasAIProvider, useFiasAIContext, FiasAIPopup } from '@/components/fias-ai';
import {
  GeneratedImage,
  ImagePreset,
  ReferenceImage,
  EMPTY_ENHANCEMENTS,
  NUM_INSTANCES,
  ARCHE_ID,
  FALLBACK_SIZE_OPTIONS,
  getOptionsFromSchema,
  formatModelName,
} from '../types/image-generator.types';
import { detectActiveMode } from '../types/image-generator-modes';
import { ImageModelsPage } from './ImageModelsPage';

/** Thin wrapper that connects FiasAIPopup to FiasAIContext */
function FiasAIPopupConnected() {
  const ctx = useFiasAIContext();
  return (
    <FiasAIPopup
      isOpen={ctx.isPanelOpen}
      onClose={ctx.closePanel}
      messages={ctx.messages}
      isStreaming={ctx.isStreaming}
      onSubmit={ctx.submit}
      onClear={ctx.reset}
      onApplyProposal={ctx.applyProposal}
      onRejectProposal={ctx.rejectProposal}
    />
  );
}

function ImageGeneratorContent() {
  // --- Page-level UI state ---
  const [inputValue, setInputValue] = useState('');
  const [lightboxImage, setLightboxImage] = useState<GeneratedImage | null>(null);
  const [infoImage, setInfoImage] = useState<GeneratedImage | null>(null);
  const [activeSettingsInstance, setActiveSettingsInstance] = useState<number | null>(null);
  const [isPresetsDialogOpen, setIsPresetsDialogOpen] = useState(false);
  const [isModesDialogOpen, setIsModesDialogOpen] = useState(false);
  const [isSavePresetDialogOpen, setIsSavePresetDialogOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [editingPreset, setEditingPreset] = useState<ImagePreset | null>(null);
  const [inlineSaveInstance, setInlineSaveInstance] = useState<number | null>(null);
  const [inlineSavePresetName, setInlineSavePresetName] = useState('');
  const [referenceDialogInstance, setReferenceDialogInstance] = useState<number | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelsContainerRef = useRef<HTMLDivElement>(null);
  const { isCollapsed, toggle } = useSidebar();
  const { user } = useAuth();

  // --- Session persistence ---
  const { updateImageGeneratorPreferences } = useArcheSession({ archeId: ARCHE_ID });

  // --- State management hook ---
  const {
    state,
    updateInstanceConfig,
    updateInstanceState,
    toggleInstance,
    toggleMinimized,
    canMinimize,
    addImage,
    removeImage,
    clearAllImages,
    addPreset,
    savePreset,
    applyPresetToInstance,
    overwritePreset,
    deletePreset,
    updatePreset,
    addTagDefinition,
    deleteTagDefinition,
    renameTagDefinition,
    setReferenceImage,
    clearReferenceImage,
    updateEnhancement,
    clearEnhancement,
    selectFromHistory,
    syncConfigsWithModels,
    applyMode,
    saveMode,
    deleteMode,
    updateMode,
  } = useImageGeneratorState({
    userId: user?.userId ?? 'anonymous',
    onPreferencesChange: updateImageGeneratorPreferences,
  });

  const {
    instanceConfigs,
    instanceStates,
    enabledInstances,
    minimizedInstances,
    instanceImages,
    enhancementHistory,
    presets,
    tagDefinitions,
    modes,
  } = state;

  const activeMode = useMemo(
    () => detectActiveMode(instanceConfigs, modes),
    [instanceConfigs, modes],
  );

  // --- Fetch available image models ---
  const { models: availableModels, loading: modelsLoading } = useImageModels();

  // Sync instance model names with available models when they load
  useEffect(() => {
    syncConfigsWithModels(availableModels);
  }, [availableModels, syncConfigsWithModels]);

  // Helper to get display name from API data
  const getModelDisplayName = useCallback(
    (modelName: string): string => {
      const model = availableModels.find((m) => m.modelName === modelName);
      return model?.displayName || formatModelName(modelName);
    },
    [availableModels],
  );

  // --- Submit hook ---
  const {
    handleSubmit: submitImages,
    handleCancel,
    handleCancelInstance,
    handleDownload,
    handleDelete,
    isAnyGenerating,
    isInstanceGenerating,
    instanceScrollRefs,
  } = useImageGeneratorSubmit({
    state,
    updateInstanceState,
    addImage,
    removeImage,
    getModelDisplayName,
    availableModels,
  });

  // --- Settings sheet: parameter schema ---
  const activeConfig =
    activeSettingsInstance !== null ? instanceConfigs[activeSettingsInstance] : null;
  const activeModel = activeConfig
    ? availableModels.find((m) => m.modelName === activeConfig.modelName)
    : null;

  const { parameterSchema, loading: schemaLoading } = useImageModelDefinition({
    provider: activeModel?.provider || 'openai',
    modelName: activeConfig?.modelName || 'dall-e-3',
    autoFetch: activeSettingsInstance !== null,
  });

  const sizeOptions = getOptionsFromSchema(parameterSchema, 'size', FALLBACK_SIZE_OPTIONS);

  // --- Form submission ---
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!inputValue.trim()) return;
      const value = inputValue;
      setInputValue('');
      await submitImages(value);
    },
    [inputValue, submitImages],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Handle Enter key globally
  useEffect(() => {
    const handleGlobalEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA';
      if (isInputFocused) return;
      if (lightboxImage || infoImage || activeSettingsInstance !== null) return;
      if (inputValue.trim()) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleGlobalEnter);
    return () => window.removeEventListener('keydown', handleGlobalEnter);
  }, [inputValue, lightboxImage, infoImage, activeSettingsInstance, handleSubmit]);

  // Handle Escape key to close lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lightboxImage) {
        setLightboxImage(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // --- Preset callbacks for settings sheet ---
  const handleSavePreset = useCallback(() => {
    if (!newPresetName.trim() || activeSettingsInstance === null) return;
    savePreset(newPresetName.trim(), activeSettingsInstance);
    setNewPresetName('');
    setIsSavePresetDialogOpen(false);
  }, [newPresetName, activeSettingsInstance, savePreset]);

  const handleSavePresetInline = useCallback(
    (instanceId: number) => {
      if (!inlineSavePresetName.trim()) return;
      savePreset(inlineSavePresetName.trim(), instanceId);
      setInlineSavePresetName('');
      setInlineSaveInstance(null);
    },
    [inlineSavePresetName, savePreset],
  );

  const handleAddPreset = useCallback(() => {
    const preset: ImagePreset = {
      id: `preset_${crypto.randomUUID()}`,
      name: 'New Preset',
      size: '1:1',
      quality: 'standard',
      style: 'vivid',
      enhancements: { ...EMPTY_ENHANCEMENTS },
      providerParams: {},
      createdAt: new Date().toISOString(),
      tags: [],
      categoryId: null,
    };
    addPreset(preset);
    setEditingPreset(preset);
  }, [addPreset]);

  const handleLoadPreset = useCallback((preset: ImagePreset) => {
    setEditingPreset(preset);
  }, []);

  const handleOverwritePreset = useCallback(
    (instanceId: number) => {
      const config = instanceConfigs[instanceId];
      if (config?.originalPresetId) {
        overwritePreset(config.originalPresetId, instanceId);
      }
    },
    [instanceConfigs, overwritePreset],
  );

  const handleLoadMode = useCallback(
    (mode: import('../types/image-generator-modes').ImageMode) => {
      applyMode(mode);
      setIsModesDialogOpen(false);
    },
    [applyMode],
  );

  const handleDismissError = useCallback(
    (instanceId: number) => {
      updateInstanceState(instanceId, { error: null, stage: 'idle', progressMessage: '' });
    },
    [updateInstanceState],
  );

  const handleStartInlineSave = useCallback((idx: number) => {
    setInlineSaveInstance(idx);
    setInlineSavePresetName('');
  }, []);

  const handleCancelInlineSave = useCallback(() => {
    setInlineSaveInstance(null);
    setInlineSavePresetName('');
  }, []);

  const handleUseAsReference = useCallback(
    (instanceId: number, image: GeneratedImage) => {
      const ref: ReferenceImage = {
        imageRef: image.imageRef,
        thumbnailUrl: image.imageUrl,
        source: 'generated',
      };
      setReferenceImage(instanceId, ref);
    },
    [setReferenceImage],
  );

  const handleOpenReferenceDialog = useCallback((instanceId: number) => {
    setReferenceDialogInstance(instanceId);
  }, []);

  const handleReferenceSelected = useCallback(
    (ref: ReferenceImage) => {
      if (referenceDialogInstance !== null) {
        setReferenceImage(referenceDialogInstance, ref);
      }
    },
    [referenceDialogInstance, setReferenceImage],
  );

  const handleClearReference = useCallback(
    (instanceId: number) => {
      clearReferenceImage(instanceId);
    },
    [clearReferenceImage],
  );

  const scrollRefCallback = useCallback(
    (idx: number) => (el: HTMLDivElement | null) => {
      instanceScrollRefs.current[idx] = el;
    },
    [instanceScrollRefs],
  );

  // --- Fias AI integration ---
  const capabilities = useImageGeneratorAI({
    state,
    updateInstanceConfig,
    toggleInstance,
    toggleMinimized,
    applyPresetToInstance,
    setReferenceImage,
    clearReferenceImage,
    availableModels,
  });

  return (
    <FiasAIProvider capabilities={capabilities}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <ImageGeneratorHeader
          isCollapsed={isCollapsed}
          onToggleSidebar={toggle}
          enabledInstances={enabledInstances}
          minimizedInstances={minimizedInstances}
          instanceConfigs={instanceConfigs}
          onToggleInstance={toggleInstance}
          onToggleMinimized={toggleMinimized}
          canMinimize={canMinimize}
          onOpenPresets={() => setIsPresetsDialogOpen(true)}
          activeMode={activeMode}
          modes={modes}
          onSelectMode={applyMode}
          onOpenModes={() => setIsModesDialogOpen(true)}
          onClearAllImages={clearAllImages}
        />

        {/* Main content area - up to 5 column layout with horizontal scroll */}
        <div ref={panelsContainerRef} className="flex-1 overflow-x-auto scrollbar-hide">
          <div className="h-full flex flex-row">
            {Array.from({ length: NUM_INSTANCES }, (_, idx) => (
              <ImageInstancePanel
                key={idx}
                index={idx}
                config={instanceConfigs[idx]}
                state={instanceStates[idx]}
                images={instanceImages[idx]}
                isEnabled={enabledInstances[idx]}
                isMinimized={minimizedInstances[idx]}
                isGenerating={isInstanceGenerating(idx)}
                availableModels={availableModels}
                modelsLoading={modelsLoading}
                presets={presets}
                canMinimize={canMinimize(idx)}
                inlineSaveInstance={inlineSaveInstance}
                inlineSavePresetName={inlineSavePresetName}
                onToggleMinimized={toggleMinimized}
                onApplyPreset={applyPresetToInstance}
                onUpdateConfig={updateInstanceConfig}
                onOpenSettings={setActiveSettingsInstance}
                onSetLightboxImage={setLightboxImage}
                onSetInfoImage={setInfoImage}
                onSetInputValue={setInputValue}
                onDownload={handleDownload}
                onDelete={handleDelete}
                onDismissError={handleDismissError}
                onCancelGeneration={handleCancelInstance}
                onOverwritePreset={handleOverwritePreset}
                onDeletePreset={deletePreset}
                onUseAsReference={handleUseAsReference}
                onOpenReferenceDialog={handleOpenReferenceDialog}
                onClearReference={handleClearReference}
                onStartInlineSave={handleStartInlineSave}
                onInlineSavePresetNameChange={setInlineSavePresetName}
                onSavePresetInline={handleSavePresetInline}
                onCancelInlineSave={handleCancelInlineSave}
                scrollRef={scrollRefCallback(idx)}
              />
            ))}
          </div>
        </div>

        {/* Custom horizontal scrollbar - only show when content overflows */}
        <ImageGeneratorScrollbar
          containerRef={panelsContainerRef}
          minimizedInstances={minimizedInstances}
        />

        {/* Settings Sheet */}
        <ImageSettingsSheet
          open={activeSettingsInstance !== null}
          onOpenChange={(open) => !open && setActiveSettingsInstance(null)}
          activeSettingsInstance={activeSettingsInstance}
          config={activeConfig}
          schemaLoading={schemaLoading}
          sizeOptions={sizeOptions}
          enhancementHistory={enhancementHistory}
          presets={presets}
          tagDefinitions={tagDefinitions}
          availableModels={availableModels}
          isSavePresetDialogOpen={isSavePresetDialogOpen}
          newPresetName={newPresetName}
          onOverwritePreset={handleOverwritePreset}
          onUpdateConfig={updateInstanceConfig}
          onUpdateEnhancement={updateEnhancement}
          onClearEnhancement={clearEnhancement}
          onSelectFromHistory={selectFromHistory}
          onOpenSavePresetDialog={() => setIsSavePresetDialogOpen(true)}
          onCloseSavePresetDialog={() => {
            setIsSavePresetDialogOpen(false);
            setNewPresetName('');
          }}
          onNewPresetNameChange={setNewPresetName}
          onSavePreset={handleSavePreset}
          onUpdatePreset={updatePreset}
          onCreateTag={addTagDefinition}
        />

        {/* Presets Dialog */}
        <ImagePresetsDialog
          open={isPresetsDialogOpen}
          onOpenChange={setIsPresetsDialogOpen}
          presets={presets}
          tagDefinitions={tagDefinitions}
          sizeOptions={sizeOptions}
          onLoadPreset={handleLoadPreset}
          onDeletePreset={deletePreset}
          onUpdatePreset={updatePreset}
          onAddPreset={handleAddPreset}
          onAddTag={addTagDefinition}
          onDeleteTag={deleteTagDefinition}
          onRenameTag={renameTagDefinition}
        />

        {/* Preset Edit Dialog */}
        <PresetEditDialog
          open={editingPreset !== null}
          onOpenChange={(open) => !open && setEditingPreset(null)}
          preset={editingPreset}
          tagDefinitions={tagDefinitions}
          availableModels={availableModels}
          onSave={updatePreset}
          onCreateTag={addTagDefinition}
        />

        {/* Modes Dialog */}
        <ModesDialog
          open={isModesDialogOpen}
          onOpenChange={setIsModesDialogOpen}
          modes={modes}
          tagDefinitions={tagDefinitions}
          availableModels={availableModels}
          onLoadMode={handleLoadMode}
          onDeleteMode={deleteMode}
          onUpdateMode={updateMode}
          onSaveMode={saveMode}
          onAddTag={addTagDefinition}
          onDeleteTag={deleteTagDefinition}
          onRenameTag={renameTagDefinition}
        />

        {/* Input area */}
        <div className="flex-shrink-0 border-t border-border bg-background">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <form onSubmit={handleSubmit} className="relative">
              <Textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe the image you want to create..."
                disabled={isAnyGenerating}
                className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
                rows={1}
                style={{ height: 'auto' }}
              />
              {isAnyGenerating ? (
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  onClick={handleCancel}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  title="Stop generation"
                >
                  <Square className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="icon"
                  disabled={!inputValue.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </form>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Press Enter to generate on all enabled instances, Shift+Enter for new line
            </p>
          </div>
        </div>

        {/* Reference Image Dialog */}
        <ReferenceImageDialog
          open={referenceDialogInstance !== null}
          onOpenChange={(open) => !open && setReferenceDialogInstance(null)}
          onReferenceSelected={handleReferenceSelected}
        />

        {/* Lightbox and Info Dialogs */}
        <ImageLightbox
          lightboxImage={lightboxImage}
          infoImage={infoImage}
          onCloseLightbox={() => setLightboxImage(null)}
          onCloseInfo={() => setInfoImage(null)}
          onDownload={handleDownload}
          getModelDisplayName={getModelDisplayName}
        />

        {/* Fias AI Popup */}
        <FiasAIPopupConnected />
      </div>
    </FiasAIProvider>
  );
}

export function ImageGeneratorArchePage() {
  return (
    <Routes>
      <Route
        path="/models"
        element={
          <ArcheLayout>
            <ImageModelsPage />
          </ArcheLayout>
        }
      />
      <Route
        path="/*"
        element={
          <ArcheLayout>
            <ImageGeneratorContent />
          </ArcheLayout>
        }
      />
    </Routes>
  );
}

export default ImageGeneratorArchePage;
